import el from "./el";
import en from "./en";

/**
 * The smallest thing that can hold two languages.
 *
 * No i18n library, for the same reason `errors.js` and `sseRegistry.js` aren't libraries either:
 * what this app needs is interpolation and a two-form plural, and both fit in the space an ICU
 * message parser would take just to load. Greek and English each have exactly one plural boundary
 * and `Intl.PluralRules` already knows where it is.
 *
 * Keys are flat and dotted — `profile.rideEmails.title` — rather than nested objects. Flat keys
 * grep: reading a screen and wanting to know where a sentence comes from is the common question,
 * and a nested catalogue answers it only by reading the tree. It also means a missing key is a
 * missing string rather than a `TypeError` on an absent branch.
 */

export const LANGUAGES = ["el", "en"];

/** Greek is the product's own language. English is the option. */
export const DEFAULT_LANGUAGE = "el";

const CATALOGS = { el, en };

/** Where the choice is mirrored for the things that run before — or outside — React. */
export const LANGUAGE_STORAGE_KEY = "tride-lang";

/**
 * The language to render in right now, before the account has had its say.
 *
 * An explicit choice if this device has seen one — made on the public pages or in Profile, or the
 * account language mirrored here at the last sign-in — and Greek otherwise.
 *
 * <strong>Deliberately not `navigator.language`.</strong> Inferring from the browser was tried and
 * removed: this is a Greek product with a Greek default, and the population it serves is largely on
 * corporate laptops imaged en-US, so the machine's setting is a poor witness to what its owner
 * reads. Guessing wrong is also invisible to the visitor — they cannot tell whether they are seeing
 * a default or a detection. Showing the product's own language and putting a visible switch beside
 * it (see `PublicControls`) is both more honest and more correctable.
 *
 * The account overrides all of this the moment `/me` answers; see `LocaleContext`.
 *
 * This lives here, in a module with no React and no imports of its own, on purpose. Several callers
 * need it and they must not be able to reach each other: `LocaleContext` (inside the tree),
 * `client.js`, `errors.js` and `geocode.js` (plain modules, one of which every request passes
 * through). Putting it in the context module instead would close the loop
 * client → LocaleContext → AuthContext → endpoints → client, and a circular import here fails as
 * an undefined interceptor at runtime rather than as an error at build time.
 */
export const storedLanguage = () => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  try {
    const v = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return LANGUAGES.includes(v) ? v : DEFAULT_LANGUAGE;
  } catch (_) {
    // Private browsing with storage disabled. The product's own language is where to land.
    return DEFAULT_LANGUAGE;
  }
};

export const storeLanguage = (lang) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch (_) { /* the choice still holds for this session */ }
};

/** `{name}` and `{n}` placeholders. An absent variable is left as-is rather than printed "undefined". */
const interpolate = (template, vars) =>
  vars
    ? template.replace(/\{(\w+)\}/g, (whole, name) => (name in vars ? String(vars[name]) : whole))
    : template;

// One rules object per language rather than one per call — `t` runs on every render of every row.
const pluralRules = {};
const pluralFormOf = (lang, n) => {
  pluralRules[lang] = pluralRules[lang] || new Intl.PluralRules(lang);
  return pluralRules[lang].select(n);
};

// Reported once per key, not once per render, or a missing string in a list would fill the console
// with the same line a hundred times and bury whatever else was there.
const warned = new Set();
const warnMissing = (key, lang) => {
  if (process.env.NODE_ENV === "production" || warned.has(key + lang)) return;
  warned.add(key + lang);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] missing "${key}" in ${lang}`);
};

/**
 * Build the `t` for one language.
 *
 * Falls back through English before giving up, so a key added to `en.js` and forgotten in `el.js`
 * shows the English sentence rather than the raw key. That is the right failure: a reader gets a
 * line they can act on in a language they may well read, instead of `rides.seatsLeft` on a button.
 * The dev-mode warning is what stops it becoming permanent.
 *
 * @param lang one of {@link LANGUAGES}
 * @returns {(key: string, vars?: object) => string}
 */
export const translator = (lang) => (key, vars) => {
  const catalog = CATALOGS[lang] || CATALOGS[DEFAULT_LANGUAGE];
  let entry = catalog[key];

  if (entry === undefined) {
    warnMissing(key, lang);
    entry = CATALOGS.en[key];
    if (entry === undefined) return key;
  }

  // A plural entry is `{ one: "...", other: "..." }`; anything else is a plain string. Selecting on
  // `vars.n` by convention keeps the call site honest — `t("x", { n: rides.length })`.
  if (typeof entry === "object") {
    const form = pluralFormOf(lang, Number(vars?.n ?? 0));
    entry = entry[form] ?? entry.other;
  }

  return interpolate(entry, vars);
};

/**
 * An amount of euros, written the way the reader writes numbers.
 *
 * `toFixed(2)` was used for this all over the app, and it always emits a decimal *point* — so a
 * Greek screen showed "3.50 €" beside copy that correctly said "0,20 €", two conventions on one
 * card. `Intl` knows that Greek uses a comma and English a point, which is the entire reason to
 * route money through here rather than through string formatting.
 *
 * The symbol is deliberately not included: the app writes "3,50 €" with the symbol trailing, which
 * is Greek convention, and `style: "currency"` would put it in front for English readers.
 *
 * @param amount euros
 * @param lang   one of {@link LANGUAGES}
 */
export const money = (amount, lang) =>
  new Intl.NumberFormat(lang || DEFAULT_LANGUAGE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

/**
 * A plain number in the reader's convention — distances, consumption, anything not money.
 *
 * @param value    the number
 * @param lang     one of {@link LANGUAGES}
 * @param decimals fixed decimal places, or omitted to let Intl decide
 */
export const number = (value, lang, decimals) =>
  new Intl.NumberFormat(lang || DEFAULT_LANGUAGE,
    decimals === undefined ? {} : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  ).format(Number(value) || 0);